import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { useToast } from '@abyss/web/hooks/useToast';
import { Button } from '@abyss/web/ui/Button';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Layout } from '@abyss/web/ui/Layout';
import { LoadingOverlay } from '@abyss/web/ui/LoadingOverlay';
import { Modal } from '@abyss/web/ui/Modal';
import { Text } from '@abyss/web/ui/Text';
import find from 'lodash/find';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { useSaveCareTeam } from '../../hooks/useCareTeam/useCareTeam';
import { useCurrentMember } from '../../hooks/useCurrentMember';
import { useHsidUuid } from '../../hooks/useHsidUuid';
import { useLagoon } from '../../hooks/useLagoon';
import { usePrimaryCare } from '../../hooks/usePrimaryCare';
import { ProviderType } from '../../models/ProviderDetails';
import { useCareTeamStore } from '../../store/useCareTeamStore';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { usePCPStore } from '../../store/usePCPStore';
import { PCPSearchStore } from '../../store/usePCPStore/pcpStore';
import { shouldShowProviderLimitBanner } from '../../utils/primaryCare.utils';
import {
  getCurrentMemberPCPorFuturePCPInfo,
  getLoggedInMember,
  isGroupMNRMember,
} from '../../utils/user.utils';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import {
  ANALYTICS_CONSTANTS,
  getIsSuperUser,
} from '../CareTeamCard/CareTeamUtils';
import { UndoAction } from '../CareTeamProviderCard/UndoAction';
import { CareCategories, ProviderSummaryTypes } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { phoneOnly } from '../ConstantsStyles';
import { ChooseLocationCardsContainer } from '../MapViewLocationsWrap/MapViewLocationsWrap.style';
import { CareTeamSelectLocationCard } from '../ProviderLocationMap/MapViewSelectLocationCard/CareTeamSelectLocationCard';
import {
  ModalSubTitle,
  cardsContainerStyle,
  wrapperSelectedStyle,
} from '../SaveProviderLocationCareTeamModal/SaveProviderLocationCareTeamModal.style';
import { toastSuccessStyle } from '../ShareThisProvider/ShareThisProvider.style';
import { getFeatureFlag } from '../Utils';
import {
  ProviderDetailsLoader,
  deriveChipValueIfMissing,
  getNameForToast,
} from './Utils';

type BookmarkButtonProps = {
  locationId?: string;
  providerId?: string;
  providerType?: string;
  fromDataCard?: boolean;
  size?: number;
  color?: string;
  providerLocationsDetailPage?: any[];
  providerDetails?: any;
  expandedProviderListView?: boolean;
  providerName?: string;
};

export const BookmarkButton: React.FC<BookmarkButtonProps> = ({
  providerId = '',
  providerType = '',
  fromDataCard = false,
  size = 24,
  color = '$primary1',
  providerDetails = {},
  providerLocationsDetailPage = [],
  providerName = '',
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [fetchDetails, setFetchDetails] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [pendingModal, setPendingModal] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [providerDetailsData, setProviderDetailsData] = useState<any>(null);
  const providerNameRef = useRef<string>('');
  const hsidUuid = useHsidUuid();
  const allConfigs = useLagoon('config');
  const loggedInMemberInfo = getLoggedInMember();
  const maximumProviderLimit = allConfigs()?.find(
    (row) => row.key === 'MAXIMUM_PROVIDER_LIMIT_FOR_CARETEAM'
  )?.value;
  const currentMember = useCurrentMember();
  const phoneScreen = useMediaQuery(phoneOnly);
  const isSuperUser = getIsSuperUser(allConfigs);
  const providerIds = providerDetails?.providerId ?? providerId;
  const {
    setShowMaximumProviderLimitBanner,
    addBookmark,
    removeBookmark,
    isBookmarked,
    careTeamProviderCount,
    bookmarkedProviders,
  } = useCareTeamStore((state) => ({
    setShowMaximumProviderLimitBanner: state.setShowMaximumProviderLimitBanner,
    addBookmark: state.addBookmark,
    removeBookmark: state.removeBookmark,
    isBookmarked: state.isBookmarked(providerIds),
    careTeamProviderCount: state.careTeamProviderCount,
    bookmarkedProviders: state.bookmarkedProviders,
  }));

  const bookmarkIconDataTestId = isBookmarked
    ? 'bookmark-action-button'
    : 'unbookmark-action-button';
  const providerDisplayName =
    providerName ||
    providerDetails?.providerName ||
    [providerDetails?.firstName, providerDetails?.lastName]
      .filter(Boolean)
      .join(' ');
  const bookmarkA11yLabel = isBookmarked
    ? `Remove ${providerDisplayName || 'provider'} from care team`
    : `Save ${providerDisplayName || 'provider'} to care team`;

  const mobileMapViewClassName = phoneScreen ? 'location-card-map-view' : '';

  const loadingTitle = t('CARE_TEAM.BOOKMARK_LOADING_MODAL.TITLE');
  const addLoadingMessage = t(
    'CARE_TEAM.BOOKMARK_LOADING_MODAL.ADD_CARE_TEAM_MESSAGE'
  );
  const removeLoadingMessage = t(
    'CARE_TEAM.BOOKMARK_LOADING_MODAL.REMOVE_CARE_TEAM_MESSAGE'
  );
  const data = useLagoon('ui-messaging')();
  const modalContent = find(data, {
    key: ConstantsLagoon.SAVE_PROVIDER_LOCATION_MODAL
      .SAVE_PROVIDER_LOCATION_MODAL_CONTENT,
  });
  const toastContent = find(data, {
    key: ConstantsLagoon.SAVE_PROVIDER_TOAST.SAVE_PROVIDER_TOAST_MESSAGE,
  });

  const removeToastContent = find(data, {
    key: ConstantsLagoon.REMOVE_PROVIDER_TOAST.REMOVE_PROVIDER_TOAST_MESSAGE,
  });
  const formattedProviderType =
    providerType === ProviderType.PROVIDER_GROUP
      ? ProviderType.MEDICAL_GROUP
      : providerType;

  const [, setCareTeamProviders] = useSaveCareTeam({
    onCompleted: (data: any) => {
      setShowLoader(false);
      if (
        data?.data?.saveCareTeam?.status === '201' ||
        data?.data?.saveCareTeam?.status === '200'
      ) {
        addBookmark(providerIds);
        toast.show({
          message: (
            <Layout.Stack
              alignItems="left"
              data-testid="save-care-team-action-alert"
            >
              <Text color="$white" fontWeight={700}>
                {`${providerNameRef.current} ${toastContent?.message}`}
              </Text>
            </Layout.Stack>
          ),
          variant: 'success',
          icon: (
            <IconSymbol
              color="$white !important"
              data-testid="save-care-team-toast-icon"
              icon="check_circle"
            />
          ),
          css: toastSuccessStyle,
        });
        adobeLinkTrackEvent({
          name: ANALYTICS_CONSTANTS.BOOKMARK_ICON,
          location: ANALYTICS_CONSTANTS.PROVIDER_SNACK_CARD,
          type: ANALYTICS_CONSTANTS.INTERNAL_TYPE,
        });
      }
    },
  });
  const [, removeCareTeamProviders] = useSaveCareTeam({
    onCompleted: (data: any) => {
      setShowLoader(false);
      if (data?.data?.saveCareTeam?.status === '200') {
        removeBookmark(providerIds);
        toast.show({
          message: (
            <UndoAction
              message={removeToastContent?.message}
              providerName={providerName}
            />
          ),
          variant: 'success',
          icon: (
            <IconSymbol
              color="$white !important"
              data-testid="remove-care-team-toast-icon"
              icon="check_circle"
            />
          ),
          css: toastSuccessStyle,
        });
      }
    },
  });
  const { coverageType, chipValue, setChipValue } = useChipStore(
    useShallow((state: ChipState) => ({
      coverageType: state.coverageType,
      chipValue: state.chipValue,
      setChipValue: state.setChipValue,
    }))
  );

  const urlParams = new URLSearchParams(window.location.search);
  const chipValueParam = urlParams.get('chipValue');
  const coverageTypeParam = urlParams.get('coverageType');
  const sectionType = urlParams.get('sectionType');
  const pcpIndicator = urlParams.get('pcpIndicator') === 'true';
  const coverageTypeToUse = coverageType || coverageTypeParam;
  const [derivedChipValue, setDerivedChipValue] = useState('');

  useEffect(() => {
    const derivedChipValueLocal = deriveChipValueIfMissing(
      chipValue,
      chipValueParam,
      coverageTypeToUse,
      sectionType,
      pcpIndicator
    );
    if (derivedChipValueLocal) {
      setDerivedChipValue(derivedChipValueLocal);
      setChipValue(derivedChipValueLocal);
    }
  }, [chipValue, chipValueParam, coverageTypeToUse, sectionType, pcpIndicator]);

  const handleProviderDetailsLoad = (data: any, isLoading: boolean) => {
    if (isLoading) return;

    const details =
      data?.facilityDetails?.providerDetails ||
      data?.providerDetails?.providerDetails;

    setProviderDetailsData(details);

    const providerLocations = details?.providerLocations || [];
    providerNameRef.current = getNameForToast(details);
    if (providerLocations?.length === 1 && isSuperUser != null) {
      setCareTeamProviders({
        variables: {
          groupNumber: currentMember?.policyNumber || '',
          providerId: providerIds,
          providerFirstName:
            formattedProviderType === ProviderSummaryTypes.PRACTITIONER
              ? details?.firstName || providerDetails?.firstName
              : details?.providerName || providerDetails?.providerName,
          providerMiddleName:
            details?.middleName || providerDetails?.middleName,
          providerLastName: details?.lastName || providerDetails?.lastName,
          providerType: formattedProviderType,
          coverageType: coverageTypeToUse,
          chipValue:
            derivedChipValue === CareCategories.PRIMARY_CARE
              ? CareCategories.MEDICAL_SPECIALISTS
              : derivedChipValue,
          originalChipValue: derivedChipValue,
          lastVisitedDate: null,
          npi: details?.npi || providerDetails?.npi,
          isUnsave: false,
          locationIds: providerLocations[0]?.locationId,
          isSuperUser,
          ...(hsidUuid && { userId: hsidUuid }),
        },
      });
      setShowModal(false);
      setFetchDetails(false);
    } else if (providerLocations?.length > 1) {
      setPendingModal(true);
      setFetchDetails(false);
    }
  };

  useEffect(() => {
    if (pendingModal && providerDetailsData?.providerLocations?.length > 1) {
      setSelectedLocations([]);
      setShowModal(true);
      setPendingModal(false);
    }
  }, [pendingModal, providerDetailsData]);

  const providerLocations = fromDataCard
    ? providerDetailsData?.providerLocations || []
    : providerLocationsDetailPage;

  useEffect(() => {
    setShowMaximumProviderLimitBanner(false);
    return () => {
      setShowMaximumProviderLimitBanner(false);
    };
  }, [setShowMaximumProviderLimitBanner]);

  const featureFlags = useLagoon('feature-flags')();
  const ENABLE_NEW_PCP_CARE_TEAM_FLOW = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NEW_PCP_CARE_TEAM_FLOW
  );

  const hidePCPCard =
    !loggedInMemberInfo?.viewPcp || isGroupMNRMember(loggedInMemberInfo);

  const handleOnClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const showBanner = shouldShowProviderLimitBanner(
      ENABLE_NEW_PCP_CARE_TEAM_FLOW,
      bookmarkedProviders.length,
      careTeamProviderCount,
      maximumProviderLimit,
      hidePCPCard
    );

    if (!isBookmarked && showBanner) {
      setShowMaximumProviderLimitBanner(true);
    } else {
      setShowLoader(true);
      setLoadingMessage(addLoadingMessage);
      if (isBookmarked && isSuperUser != null) {
        setLoadingMessage(removeLoadingMessage);
        removeCareTeamProviders({
          variables: {
            groupNumber: currentMember?.policyNumber || '',
            providerId: providerIds,
            isSuperUser,
            isUnsave: true,
          },
        });
        setShowMaximumProviderLimitBanner(false);
        return;
      }
      if (fromDataCard) {
        setFetchDetails(true);
      } else {
        providerNameRef.current = getNameForToast(providerDetails);
        if (providerLocations?.length === 1 && isSuperUser != null) {
          setShowLoader(true);
          setCareTeamProviders({
            variables: {
              groupNumber: currentMember?.policyNumber || '',
              providerId: providerIds,
              providerFirstName:
                providerDetails?.firstName || providerDetails?.providerName,
              providerMiddleName: providerDetails?.middleName,
              providerLastName: providerDetails?.lastName,
              providerType: formattedProviderType,
              coverageType: coverageTypeToUse,
              chipValue:
                derivedChipValue === CareCategories.PRIMARY_CARE
                  ? CareCategories.MEDICAL_SPECIALISTS
                  : derivedChipValue,
              originalChipValue: derivedChipValue,
              lastVisitedDate: null,
              npi: providerDetails?.npi,
              isUnsave: false,
              locationIds: providerLocations[0]?.locationId,
              isSuperUser,
              ...(hsidUuid && { userId: hsidUuid }),
            },
          });
          setShowModal(false);
        } else if (providerLocations?.length > 1) {
          setSelectedLocations([]);
          setShowModal(true);
        }
      }
    }
  };

  const toggleLocationSelection = (locationId: string) => {
    setSelectedLocations((prevSelected) =>
      prevSelected.includes(locationId)
        ? prevSelected.filter((id) => id !== locationId)
        : [...prevSelected, locationId]
    );
  };

  const handleSaveLocations = () => {
    if (selectedLocations?.length > 0 && isSuperUser != null) {
      setCareTeamProviders({
        variables: {
          groupNumber: currentMember?.policyNumber || '',
          providerId: providerIds,
          providerFirstName:
            formattedProviderType === ProviderSummaryTypes.PRACTITIONER
              ? providerDetailsData?.firstName || providerDetails?.firstName
              : providerDetailsData?.providerName ||
                providerDetails?.providerName,
          providerMiddleName:
            providerDetailsData?.middleName || providerDetails?.middleName,
          providerLastName:
            providerDetailsData?.lastName || providerDetails?.lastName,
          providerType: formattedProviderType,
          coverageType: coverageTypeToUse,
          chipValue:
            derivedChipValue === CareCategories.PRIMARY_CARE
              ? CareCategories.MEDICAL_SPECIALISTS
              : derivedChipValue,
          originalChipValue: derivedChipValue,
          lastVisitedDate: null,
          npi: providerDetailsData?.npi || providerDetails?.npi,
          isUnsave: false,
          locationIds: selectedLocations,
          isSuperUser,
          ...(hsidUuid && { userId: hsidUuid }),
        },
      });
    }
    setShowModal(false);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setShowLoader(false);
  };

  const { data: primaryCareData } = usePrimaryCare();
  const loggedInMember = getLoggedInMember();
  const primaryCare = primaryCareData?.primaryCare;

  const memberPCPInfo = getCurrentMemberPCPorFuturePCPInfo(
    loggedInMember?.demographics?.dependentSeqNbr,
    primaryCare
  );
  const normalize = (id?: string | number) =>
    id ? String(id).replace(/\s+/g, '').replace(/^0+/, '') : '';

  const isCurrentPCP =
    !!memberPCPInfo?.providerID &&
    normalize(memberPCPInfo.providerID) === normalize(providerIds);

  const isFuturePCP =
    !!memberPCPInfo?.futurePCP?.providerID &&
    normalize(memberPCPInfo?.futurePCP?.providerID) === normalize(providerIds);

  const { choosePCP = false } = usePCPStore(
    useShallow((state: PCPSearchStore) => ({
      choosePCP: state.pcpSearchState.choosePCP,
    }))
  );

  if (isCurrentPCP || choosePCP || isFuturePCP) return null;

  return (
    <React.Fragment>
      {showLoader && (
        <div
          data-testid="loader-container"
          onClick={(e) => e.stopPropagation()}
        >
          <Modal
            css={{
              'abyss-modal-body': {
                width: '324px',
                height: '156px',
                overflowY: 'hidden',
              },
              'abyss-modal-content-container': {
                width: '324px',
                height: '156px',
              },
              'abyss-modal-header-container': {
                display: 'none',
              },
            }}
            isOpen={showLoader}
            onClose={() => {}}
            title="Loading"
          >
            <LoadingOverlay
              css={{
                'abyss-loading-overlay-dialog': {
                  width: '100%',
                  height: '100&',
                },
              }}
              isLoading
              loadingMessage={loadingMessage}
              loadingTitle={loadingTitle}
            ></LoadingOverlay>
          </Modal>
        </div>
      )}
      <button
        aria-label={bookmarkA11yLabel}
        aria-pressed={isBookmarked}
        onClick={handleOnClick}
        style={{
          alignItems: 'center',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          padding: 0,
        }}
        type="button"
      >
        <IconSymbol
          color={color}
          data-auto-testid={bookmarkIconDataTestId}
          data-testid={bookmarkIconDataTestId}
          icon="bookmark"
          size={size}
          variant={isBookmarked ? 'filled' : 'outlined'}
        />
      </button>
      {fetchDetails && (
        <ProviderDetailsLoader
          onLoad={handleProviderDetailsLoad}
          providerId={providerIds}
          providerType={formattedProviderType}
        />
      )}
      {showModal && (
        <div
          data-testid="save-provider-location-modal"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
          }}
        >
          <Modal
            data-testid="save-provider-location-modal"
            footer={
              <Modal.Section>
                <Layout.Group alignLayout="left">
                  <Button
                    data-testid="continue-button"
                    disabled={selectedLocations?.length === 0} // Disable button if no locations are selected
                    onClick={handleSaveLocations}
                  >
                    {t('SAVE_PROVIDER_LOCATION_MODAL.SAVE_BUTTON')}
                  </Button>
                  <Button
                    data-auto-testid="cancel-button"
                    data-testid="cancel-button"
                    onClick={() => {
                      setShowLoader(false);
                      handleModalClose();
                    }}
                    variant="outline"
                  >
                    {t('SAVE_PROVIDER_LOCATION_MODAL.CANCEL_BUTTON')}
                  </Button>
                </Layout.Group>
              </Modal.Section>
            }
            isOpen={showModal}
            onClose={handleModalClose}
            scrollableFocus
            title={modalContent?.title}
            titleAlign="left"
          >
            <Modal.Section>
              <ModalSubTitle>{modalContent?.message}</ModalSubTitle>
              <ChooseLocationCardsContainer
                alignItems="flex-end"
                aria-label={t('ACCESSIBILITY_LABELS.LOCATIONS')}
                className="choose-location-container"
                css={cardsContainerStyle}
                role="group"
              >
                {providerLocations?.map((location, index) => (
                  <div
                    className="location-cards-container"
                    data-auto-testid="choose-location-section"
                    data-testid="choose-location-section"
                    key={
                      location?.locationId || `details-location-${index + 1}`
                    }
                    style={{
                      padding: '2px',
                      ...(selectedLocations?.includes(location?.locationId)
                        ? wrapperSelectedStyle
                        : {}),
                    }}
                  >
                    <CareTeamSelectLocationCard
                      className={`${mobileMapViewClassName} ${
                        selectedLocations?.includes(location?.locationId)
                          ? 'highlight'
                          : ''
                      }`}
                      handleDirectionClick={() => {}}
                      index={index}
                      isDisabled={
                        selectedLocations?.length >= 5 &&
                        !selectedLocations?.includes(location?.locationId)
                      }
                      location={location}
                      selectLocation={() =>
                        toggleLocationSelection(location?.locationId)
                      }
                      selected={selectedLocations?.includes(
                        location?.locationId
                      )}
                      showBadgeRow
                      totalLocations={providerLocations?.length}
                    />
                  </div>
                ))}
              </ChooseLocationCardsContainer>
            </Modal.Section>
          </Modal>
        </div>
      )}
    </React.Fragment>
  );
};
